import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Smile,
  TrendingUp,
  Users,
  AlertTriangle,
  Star,
  CheckCircle,
  XCircle,
  Angry,
  Frown,
  Meh,
  Laugh,
} from 'lucide-react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { Grid, Card } from '../../styles/GlobalStyles';
import { StatsCard } from './StatsCard';
import climaDataJson from '../../data/clima-organizacional-data.json';

// Configuração de tema para os gráficos
const chartTheme = {
  background: '#FFFFFF',
  textColor: '#1E293B',
  fontSize: 14,
  axis: {
    domain: {
      line: {
        stroke: '#E2E8F0',
        strokeWidth: 1,
      },
    },
    legend: {
      text: {
        fontSize: 16,
        fontWeight: 600,
        fill: '#475569',
      },
    },
    ticks: {
      line: {
        stroke: '#E2E8F0',
        strokeWidth: 1,
      },
      text: {
        fontSize: 13,
        fill: '#64748B',
        fontWeight: 500,
      },
    },
  },
  grid: {
    line: {
      stroke: '#F1F5F9',
      strokeWidth: 1,
    },
  },
  legends: {
    title: {
      text: {
        fontSize: 15,
        fontWeight: 600,
        fill: '#475569',
      },
    },
    text: {
      fontSize: 13,
      fontWeight: 500,
      fill: '#64748B',
    },
  },
  tooltip: {
    container: {
      background: '#FFFFFF',
      color: '#1E293B',
      fontSize: 14,
      borderRadius: '8px',
      boxShadow:
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #E2E8F0',
    },
  },
};

const ClimaContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`;

const ContentWrapper = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ChartCard = styled(Card)`
  height: 600px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 500px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 450px;
  }
`;

const LargeChartCard = styled(ChartCard)`
  height: 700px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 600px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 550px;
  }
`;

const TallChartCard = styled(ChartCard)`
  height: 650px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 600px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 550px;
  }
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg}
    0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  padding: 1.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem;
    min-height: 250px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0.5rem;
    min-height: 200px;
  }
`;

const NPSContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
  padding: 1rem;
  gap: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    padding: 0.5rem;
    gap: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    padding: 0.5rem;
    gap: 0.5rem;
  }
`;

const NPSGaugeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50%;
  height: 100%;
  transform: translateY(-30px); /* Move para cima considerando o título */

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    transform: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    transform: none;
  }
`;

const NPSGaugeContainer = styled.div`
  position: relative;
  width: 400px;
  height: 300px;
  margin-bottom: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 320px;
    height: 240px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 280px;
    height: 220px;
    margin-bottom: 0.5rem;
  }
`;

const NPSLabels = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  width: 50%;
  height: 100%;
  padding: 2rem;
  transform: translateY(-30px); /* Move para cima considerando o título */

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    transform: none;
    padding: 1rem;
    gap: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    transform: none;
    padding: 0.5rem;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

const NPSLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;

  .percentage {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.25rem;
  }

  .label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #64748b;
  }
`;

const NPSLabelCircle = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ color }) => color}15;
  border: 2px solid ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const RiskCard = styled(ChartCard)`
  text-align: center;
`;

const RiskContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const RiskLevel = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #0277bd;
  margin-bottom: 1rem;
`;

const RiskDescription = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.6;
  text-align: center;
  max-width: 280px;
`;

const MetricsGrid = styled(Grid)`
  margin-bottom: 3rem;
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const RiskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: ${({ theme }) => theme.spacing.lg};
  flex: 1;
  height: 100%;
  justify-content: space-evenly;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.md};
    gap: 0.75rem;
  }
`;

const RiskItem = styled.div<{ priority: number }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  background: transparent;
  border-bottom: 1px solid #f1f5f9;
  width: 100%;
  box-sizing: border-box;
  flex: 1;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1rem 0;
    gap: 0.75rem;
  }
`;

const RiskNumber = styled.div<{ priority: number }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ priority }) =>
    priority === 1
      ? '#dc2626'
      : priority === 2
      ? '#f59e0b'
      : priority === 3
      ? '#eab308'
      : priority === 4
      ? '#f97316'
      : '#ef4444'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
`;

const RiskText = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #000000;
  flex: 1;
  word-wrap: break-word;
  line-height: 1.4;
`;

const DepartmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: ${({ theme }) => theme.spacing.lg};
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.md};
    gap: 0.75rem;
  }
`;

const DepartmentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1rem 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const DepartmentName = styled.div`
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
`;

const DepartmentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DepartmentFeedback = styled.div<{ type: 'positive' | 'negative' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${({ type }) => (type === 'positive' ? '#059669' : '#dc2626')};
`;

const DepartmentRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StarRating = styled.div`
  display: flex;
  gap: 0.125rem;
`;

const RatingText = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
`;

const FirstRowGrid = styled.div`
  display: grid;
  grid-template-columns: calc(70% - 1rem) calc(30% - 1rem);
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

// Obter dados do JSON
const climaData =
  climaDataJson.EMPRESA_DEMONSTRACAO.todos.todos.todos.todos.todos;

export const ClimaOrganizacional: React.FC = () => {
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const humorLineData = [
    {
      id: 'humor',
      color: '#8b5cf6',
      data: climaData.humorData,
    },
  ];

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} size={16} fill="#fbbf24" color="#fbbf24" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Star
            key={i}
            size={16}
            fill="#fbbf24"
            color="#fbbf24"
            style={{ opacity: 0.5 }}
          />
        );
      } else {
        stars.push(<Star key={i} size={16} color="#d1d5db" />);
      }
    }
    return stars;
  };

  return (
    <ClimaContainer>
      <ContentWrapper>
        <PageHeader>
          <PageTitle>Clima organizacional</PageTitle>
        </PageHeader>

        {/* Primeira linha: eNPS e Índice de Risco */}
        <FirstRowGrid>
          {/* eNPS */}
          <ChartCard style={{ height: '580px' }}>
            <ChartTitle>
              <Smile size={24} />
              eNPS
            </ChartTitle>
            <ChartContainer style={{ minHeight: '480px' }}>
              <NPSContainer>
                <NPSGaugeSection>
                  <NPSGaugeContainer>
                    <ResponsivePie
                      data={[
                        { id: 'muito-ruim', value: 20, color: '#dc2626' }, // 1-2
                        { id: 'ruim', value: 20, color: '#f59e0b' }, // 3-4
                        { id: 'neutro', value: 20, color: '#eab308' }, // 5-6
                        { id: 'bom', value: 20, color: '#10b981' }, // 7-8
                        { id: 'muito-bom', value: 20, color: '#3b82f6' }, // 9-10
                      ]}
                      margin={{
                        top: screenWidth <= 768 ? 60 : 80,
                        right: screenWidth <= 768 ? 40 : 60,
                        bottom: screenWidth <= 768 ? 80 : 100,
                        left: screenWidth <= 768 ? 40 : 60,
                      }}
                      startAngle={-90}
                      endAngle={90}
                      innerRadius={0.65}
                      padAngle={0}
                      cornerRadius={0}
                      activeOuterRadiusOffset={0}
                      colors={{ datum: 'data.color' }}
                      borderWidth={2}
                      borderColor="#ffffff"
                      enableArcLabels={false}
                      enableArcLinkLabels={false}
                      theme={{
                        ...chartTheme,
                        background: 'transparent',
                      }}
                      animate={true}
                      motionConfig="gentle"
                      layers={[
                        'arcs',
                        ({ centerX, centerY, radius }) => (
                          <g>
                            {/* Score abaixo do gauge */}
                            <text
                              x={centerX}
                              y={centerY + 50}
                              textAnchor="middle"
                              dominantBaseline="central"
                              style={{
                                fontSize: '3rem',
                                fontWeight: 800,
                                fill: '#1e293b',
                              }}
                            >
                              {climaData.nps.score}
                            </text>

                            {/* Ponteiro - apontando para posição 71 de 100 considerando início em -180° */}
                            <g transform={`translate(${centerX}, ${centerY})`}>
                              <line
                                x1="0"
                                y1="0"
                                x2={
                                  Math.cos(
                                    ((-180 +
                                      ((climaData.nps.score * 10) / 100) *
                                        180) *
                                      Math.PI) /
                                      180
                                  ) *
                                  (radius * 0.75)
                                }
                                y2={
                                  Math.sin(
                                    ((-180 +
                                      ((climaData.nps.score * 10) / 100) *
                                        180) *
                                      Math.PI) /
                                      180
                                  ) *
                                  (radius * 0.75)
                                }
                                stroke="#1e293b"
                                strokeWidth="4"
                                strokeLinecap="round"
                              />
                              <circle cx="0" cy="0" r="6" fill="#1e293b" />
                            </g>

                            {/* DEBUG: Ícones posicionados considerando início em -180° */}
                            {/* Semicírculo de -180° a 0° = 180° total, dividido em 5 segmentos de 36° cada */}
                            {/* 1º segmento: muito-ruim (vermelho) - centro em -162° */}
                            <g
                              transform={`translate(${
                                centerX +
                                (radius + 20) * Math.cos((-162 * Math.PI) / 180)
                              }, ${
                                centerY +
                                (radius + 20) * Math.sin((-162 * Math.PI) / 180)
                              })`}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="15"
                                fill="#fee2e2"
                                stroke="#dc2626"
                                strokeWidth="2"
                              />
                              <foreignObject
                                x="-10"
                                y="-10"
                                width="20"
                                height="20"
                              >
                                <Angry size={20} color="#dc2626" />
                              </foreignObject>
                            </g>

                            {/* 2º segmento: ruim (laranja) - centro em -126° */}
                            <g
                              transform={`translate(${
                                centerX +
                                (radius + 20) * Math.cos((-126 * Math.PI) / 180)
                              }, ${
                                centerY +
                                (radius + 20) * Math.sin((-126 * Math.PI) / 180)
                              })`}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="15"
                                fill="#fed7aa"
                                stroke="#f59e0b"
                                strokeWidth="2"
                              />
                              <foreignObject
                                x="-10"
                                y="-10"
                                width="20"
                                height="20"
                              >
                                <Frown size={20} color="#f59e0b" />
                              </foreignObject>
                            </g>

                            {/* 3º segmento: neutro (amarelo) - centro em -90° */}
                            <g
                              transform={`translate(${
                                centerX +
                                (radius + 20) * Math.cos((-90 * Math.PI) / 180)
                              }, ${
                                centerY +
                                (radius + 20) * Math.sin((-90 * Math.PI) / 180)
                              })`}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="15"
                                fill="#fef3c7"
                                stroke="#eab308"
                                strokeWidth="2"
                              />
                              <foreignObject
                                x="-10"
                                y="-10"
                                width="20"
                                height="20"
                              >
                                <Meh size={20} color="#eab308" />
                              </foreignObject>
                            </g>

                            {/* 4º segmento: bom (verde) - centro em -54° */}
                            <g
                              transform={`translate(${
                                centerX +
                                (radius + 20) * Math.cos((-54 * Math.PI) / 180)
                              }, ${
                                centerY +
                                (radius + 20) * Math.sin((-54 * Math.PI) / 180)
                              })`}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="15"
                                fill="#dcfce7"
                                stroke="#10b981"
                                strokeWidth="2"
                              />
                              <foreignObject
                                x="-10"
                                y="-10"
                                width="20"
                                height="20"
                              >
                                <Smile size={20} color="#10b981" />
                              </foreignObject>
                            </g>

                            {/* 5º segmento: muito-bom (azul) - centro em -18° */}
                            <g
                              transform={`translate(${
                                centerX +
                                (radius + 20) * Math.cos((-18 * Math.PI) / 180)
                              }, ${
                                centerY +
                                (radius + 20) * Math.sin((-18 * Math.PI) / 180)
                              })`}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="15"
                                fill="#dbeafe"
                                stroke="#3b82f6"
                                strokeWidth="2"
                              />
                              <foreignObject
                                x="-10"
                                y="-10"
                                width="20"
                                height="20"
                              >
                                <Laugh size={20} color="#3b82f6" />
                              </foreignObject>
                            </g>
                          </g>
                        ),
                      ]}
                    />
                  </NPSGaugeContainer>
                </NPSGaugeSection>

                <NPSLabels>
                  <NPSLabel>
                    <NPSLabelCircle color="#10b981">
                      <Smile size={32} color="#10b981" />
                    </NPSLabelCircle>
                    <div className="percentage">
                      {climaData.nps.promotores}%
                    </div>
                    <div className="label">Promotores</div>
                  </NPSLabel>
                  <NPSLabel>
                    <NPSLabelCircle color="#eab308">
                      <Meh size={32} color="#eab308" />
                    </NPSLabelCircle>
                    <div className="percentage">{climaData.nps.neutros}%</div>
                    <div className="label">Neutros</div>
                  </NPSLabel>
                  <NPSLabel>
                    <NPSLabelCircle color="#dc2626">
                      <Angry size={32} color="#dc2626" />
                    </NPSLabelCircle>
                    <div className="percentage">
                      {climaData.nps.detratores}%
                    </div>
                    <div className="label">Detratores</div>
                  </NPSLabel>
                </NPSLabels>
              </NPSContainer>
            </ChartContainer>
          </ChartCard>

          {/* Índice de Risco Psicossocial */}
          <RiskCard style={{ height: '580px' }}>
            <ChartTitle>
              <AlertTriangle size={24} />
              Índice de risco psicossocial
            </ChartTitle>
            <RiskContent>
              <RiskLevel>{climaData.riscoGeral.nivel}</RiskLevel>
              <RiskDescription>
                {climaData.riscoGeral.descricao}
              </RiskDescription>
            </RiskContent>
          </RiskCard>
        </FirstRowGrid>

        {/* Segunda linha: Métricas de Estresse e Retenção */}
        <MetricsGrid
          columns={3}
          minColumnWidth="300px"
          style={{ marginBottom: '3rem' }}
        >
          <StatsCard
            title="Picos de humor"
            value={climaData.metricas.picosHumor.value}
            icon={<TrendingUp size={28} />}
            color="#8b5cf6"
            bgColor="#8b5cf6"
            iconColor="#FFFFFF"
          />
          <StatsCard
            title="Nível de Estresse/Ansiedade"
            value={`${climaData.metricas.nivelEstresse.value}%`}
            subtitle={`↓ ${Math.abs(climaData.metricas.nivelEstresse.trend)}% ${
              climaData.metricas.nivelEstresse.description
            }`}
            icon={<AlertTriangle size={28} />}
            color="#f59e0b"
            bgColor="#f59e0b"
            iconColor="#FFFFFF"
          />
          <StatsCard
            title="Taxa de Retenção de Colaboradores"
            value={`${climaData.metricas.taxaRetencao.value}%`}
            subtitle={`↑ ${climaData.metricas.taxaRetencao.trend}% ${climaData.metricas.taxaRetencao.description}`}
            icon={<Users size={28} />}
            color="#10b981"
            bgColor="#10b981"
            iconColor="#FFFFFF"
          />
        </MetricsGrid>

        {/* Terceira linha: Picos de humor */}
        <LargeChartCard style={{ marginBottom: '3rem' }}>
          <ChartTitle>
            <TrendingUp size={24} />
            Picos de humor
          </ChartTitle>
          <ChartContainer>
            <ResponsiveLine
              data={humorLineData}
              margin={{
                top: 30,
                right: screenWidth <= 768 ? 40 : 60,
                bottom: screenWidth <= 768 ? 120 : 110,
                left: screenWidth <= 768 ? 50 : 80,
              }}
              xScale={{ type: 'point' }}
              yScale={{
                type: 'linear',
                min: 6,
                max: 9,
                stacked: false,
                reverse: false,
              }}
              yFormat=" >-.1f"
              curve="catmullRom"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: screenWidth <= 768 ? -45 : 0,
                legend: 'Período',
                legendOffset: screenWidth <= 768 ? 80 : 70,
                legendPosition: 'middle',
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: screenWidth <= 768 ? '' : 'Índice de Humor',
                legendOffset: screenWidth <= 768 ? -40 : -60,
                legendPosition: 'middle',
              }}
              enableGridX={false}
              enableGridY={true}
              pointSize={screenWidth <= 768 ? 8 : 10}
              pointColor="#8b5cf6"
              pointBorderWidth={screenWidth <= 768 ? 2 : 3}
              pointBorderColor="#FFFFFF"
              pointLabelYOffset={-12}
              enableArea={false}
              useMesh={true}
              colors={['#8b5cf6']}
              lineWidth={screenWidth <= 768 ? 3 : 4}
              animate={true}
              motionConfig="gentle"
              theme={chartTheme}
              enableSlices="x"
            />
          </ChartContainer>
        </LargeChartCard>

        {/* Quarta linha: Riscos Psicossociais e Satisfação por Departamento */}
        <MetricsGrid columns={2} minColumnWidth="600px">
          {/* Principais riscos psicossociais identificados */}
          <TallChartCard>
            <ChartTitle>
              <AlertTriangle size={24} />
              Principais riscos psicossociais identificados
            </ChartTitle>
            <RiskList>
              {climaData.riscosPsicossociais.map((risco, index) => (
                <RiskItem key={index} priority={index + 1}>
                  <RiskNumber priority={index + 1}>{index + 1}</RiskNumber>
                  <RiskText>{risco}</RiskText>
                </RiskItem>
              ))}
            </RiskList>
          </TallChartCard>

          {/* Nível de satisfação por setor/departamento */}
          <TallChartCard style={{ overflow: 'hidden' }}>
            <ChartTitle>
              <Users size={24} />
              Nível de satisfação por setor/departamento
            </ChartTitle>
            <DepartmentList>
              {climaData.departamentos.map((dept, index) => (
                <DepartmentItem key={index}>
                  <DepartmentInfo>
                    <DepartmentName>{dept.nome}</DepartmentName>
                    <DepartmentFeedback type="positive">
                      <CheckCircle size={16} />
                      {dept.feedback.positivo}
                    </DepartmentFeedback>
                    <DepartmentFeedback type="negative">
                      <XCircle size={16} />
                      {dept.feedback.negativo}
                    </DepartmentFeedback>
                  </DepartmentInfo>
                  <DepartmentRating>
                    <StarRating>{renderStars(dept.satisfacao)}</StarRating>
                    <RatingText>{dept.satisfacao}/5</RatingText>
                  </DepartmentRating>
                </DepartmentItem>
              ))}
            </DepartmentList>
          </TallChartCard>
        </MetricsGrid>
      </ContentWrapper>
    </ClimaContainer>
  );
};
